import { List, Set as ImmutableSet } from 'immutable';
import { ContactFull } from './Contact';
import { ExtensionKey, QueueKey, TagKey, TicketKey } from './EntityKeys';
import { TelephoneNumber } from './TelephoneNumber';
import { TicketEvents } from './TicketEventData';
import { TypedMap } from './TypedMap';

export const UNASSIGNED_ASSIGNEE_VALUE = 'unassigned';

export type TicketSortField = 'lastUpdate' | 'createdDate';
export type TicketSortDirection = 'asc' | 'desc';

export interface TicketSort {
    field: TicketSortField;
    direction: TicketSortDirection;
}

// Default ticket-list sort, shared by the reducer (plain object) and the
// selectors (wrapped in `fromJS`). Single source of truth for both.
export const DEFAULT_TICKETS_SORT: TicketSort = {
    field: 'lastUpdate',
    direction: 'desc',
};

// A queue with no explicit default resolves to private, matching the backend.
export const DEFAULT_TICKET_VISIBILITY: TicketVisibility = 'private';

export interface DefaultTicketVisibility {
    queueEntityKey: QueueKey;
    visibility: TicketVisibility;
}

export interface Tickets {
    openTicketsUnassignedKeysData: TypedMap<KeysData>;
    openTicketsAssignedToUserKeysData: TypedMap<KeysData>;
    closedTicketsKeysData: TypedMap<KeysData>;
    ticketsAllKeysData: TypedMap<KeysData>;
    tempSearchData: TypedMap<TempSearchData>;
    metadata: any;
    settings: TypedMap<Settings>;
    ticketEvents: TypedMap<TicketEvents>;
    relatedKeysData: TypedMap<Record<TicketKey, TypedMap<KeysData>>>;
    ticketsData: TypedMap<TicketsData>;
    componentsMountStatus: TypedMap<ComponentsMountStatus>;
    drafts?: TypedMap<Record<TicketKey, TypedMap<TicketDrafts>>>;
    ticketKeysByLocutusSessionId?: TypedMap<Record<string, TicketKey>>;
}

interface TicketDrafts {
    internal?: TypedMap<DraftContent>;
    external?: TypedMap<DraftContent>;
}

interface DraftContent {
    text: string;
    mentions?: List<TypedMap<PickedMention>>;
}

// A colleague picked in the mention picker, and the index of the "@" of their
// mention in the note. The note keeps the pick, so the mention goes to this
// colleague and not to another with the same name.
export interface PickedMention {
    extensionKey: ExtensionKey;
    name: string;
    start: number;
}

export type TicketReplyType = keyof TicketDrafts;

interface TempSearchData {
    byKey: TypedMap<Record<TicketKey, TypedMap<Ticket>>>;
    nextPageNumber?: number;
    apiHasMoreData?: boolean;
}

interface KeysData {
    keys: TypedMap<Record<TicketKey, TypedMap<SortingData>>>;
    nextPageNumber?: number;
    apiHasMoreData?: boolean;
}

export interface SortingData {
    key: TicketKey;
    createdDate: string;
    lastUpdate: string;
}

// Runtime list of SortingData's keys — needed because the guard below runs at
// runtime, where types are erased. `satisfies` rejects a typo or a key that no
// longer exists on SortingData. Adding a NEW key to SortingData isn't enforced
// here, so add it to this list too.
export const sortingDataKeys = [
    'key',
    'createdDate',
    'lastUpdate',
] as const satisfies readonly (keyof SortingData)[];

export const isIncludedInTicketsSortingDataInterface = (
    property: string,
): property is keyof SortingData =>
    (sortingDataKeys as readonly string[]).includes(property);

interface Settings {
    ticketsTabIsEnforced: boolean;
    emailSignaturesEnabled: boolean;
    perQueue: TypedMap<Record<QueueKey, TypedMap<TicketQueueSettings>>>;
    defaultVisibilityPerQueue?: TypedMap<
        Record<QueueKey, TypedMap<DefaultTicketVisibility>>
    >;
    attachments?: TypedMap<
        Record<TicketMessageChannel, TypedMap<FileRestrictions>>
    >;
    sort?: TypedMap<TicketSort>;
}

interface FileRestrictions {
    blockedMimetypes: List<string>;
    allowedMimetypes: TypedMap<Record<string, TypedMap<AllowedMimetype>>>;
}

export interface AllowedMimetype {
    maxSizeBytes: number;
}

export type TicketMessageChannel = 'internal' | 'email' | 'whatsapp' | 'sms';

export type TicketStatus = 'open' | 'closed';

/**
 * Who may read a ticket. 'private' is member-only; 'public' widens *read* access
 * to every user in the customer. Writes stay member-gated either way — the one
 * exception being internal notes, which anyone with read access may post.
 *
 * Lower-case to match the wire format, which mirrors how `state` is serialized on
 * the same DTO.
 */
export type TicketVisibility = 'public' | 'restricted' | 'private';

export const ticketVisibilities = [
    'public',
    'restricted',
    'private',
] as const satisfies readonly TicketVisibility[];

export const isTicketVisibility = (value: unknown): value is TicketVisibility =>
    (ticketVisibilities as readonly unknown[]).includes(value);

/**
 * What a control displays: the three tiers, plus a tier this client cannot name.
 *
 * The DTO carries a fourth value for exactly that — `UNKNOWN`, which client-server
 * deserializes any tier it does not recognise to — so a newer backend reaches an
 * older client with a value outside the three. Displaying it as private would state
 * that general access is off, which may be less access than the ticket really grants.
 * Nothing sends this value back: a change comes from the three offered tiers.
 */
export type TicketVisibilityForDisplay = TicketVisibility | 'unknown';

/**
 * What the logged-in user may do to this ticket, resolved by the backend and sent
 * per ticket. The client renders these; it does not recompute them from capability,
 * membership and visibility — those are inputs the resolver has already consumed.
 *
 * One key per act rather than a single `canWrite`, because the rights genuinely
 * diverge: with general access on, a colleague may post an internal note but not
 * reply to the bPart, and an email ticket's subject is frozen for everyone.
 *
 * `setState` is one key covering both directions. Only one transition is ever legal
 * from a binary state, so a second key would always be meaningless — and "reopen"
 * would assert a history a ticket auto-closed on a missed call may not have.
 */
export interface TicketViewerPermissions {
    reply: boolean;
    addInternalNote: boolean;
    assign: boolean;
    setState: boolean;
    setSubject: boolean;
    editTags: boolean;
    forwardToQueue: boolean;
    setAwaitingActivity: boolean;
    manageMembers: boolean;
    setVisibility: boolean;
}

export type TicketPermission = keyof TicketViewerPermissions;

interface TicketsData {
    ticketsBadgeCount: number;
    totalNumberOfTicketsOpenAssignedToUser: number;
    totalNumberOfRelatedTickets: TypedMap<Record<TicketKey, number>>;
    byKey: TypedMap<Record<TicketKey, TypedMap<Ticket>>>;
}

export interface Ticket {
    visitorDetails: TypedMap<ContactFull>;
    state: TicketStatus;
    assignee: ExtensionKey | 'unassigned' | null;
    lastUpdate: string;
    createdDate: string;
    // A set, not a list: membership has no order that anything depends on (the
    // members list sorts by name at render) and no duplicates, and the lookup is
    // flat rather than a scan — which matters wherever membership is tested per
    // row of a ticket list.
    members: ImmutableSet<ExtensionKey>;
    origin: TypedMap<TicketOrigin>;
    key: TicketKey;
    awaitingActivity?: boolean;
    subject?: string;
    visibility?: TicketVisibility;
    // Optional because a ticket cached before this deploy, or a partial DTO, carries
    // none. Read it through `selectOwnUserHasTicketPermission` in
    // ticket-permission-selectors, never directly:
    // absent denies, and nothing may AND a capability check back on top.
    viewerPermissions?: TypedMap<TicketViewerPermissions>;
}

export interface ComponentsMountStatus {
    ticketsList: TypedMap<TicketsListMountStatus>;
    ticketView: TypedMap<Record<TicketKey, TypedMap<TicketViewMountStatus>>>;
}

export interface TicketViewMountStatus {
    context: 'page' | 'modal';
    ticketKey: TicketKey;
}

interface TicketsListMountStatus {
    isMounted: boolean;
    tabSelected: string;
}

interface TicketOrigin {
    // May be unknown to this client; read it via flow/utils/ticket-channel-utils.
    DTOSubtype: string;
    visitorId: TypedMap<TelephoneNumber>;
    visitorDisplayName: string;
    type: string;
    locutusSessionId: string;
    key: string;
}

type TicketQueueSettingsKey = `ticketsetting-${number}`;
export interface TicketQueueSettings {
    autoCreateTicket: 'ON_ALL_CALLS' | 'NEVER' | 'ON_MISSED_CALLS';
    closeOnHangUp: boolean;
    closeOnMissedCall: boolean;
    key: TicketQueueSettingsKey;
}

export interface AdvancedSearchFilters {
    state?: 'open' | 'closed';
    marked: boolean;
    since: string;
    assignee?: ExtensionKey | typeof UNASSIGNED_ASSIGNEE_VALUE;
    tagKeysOr?: TagKey[];
}

export interface TicketsSearchParams {
    searchQuery: string;
    assignee?: string;
    state?: 'open' | 'closed';
    since?: number;
    countOnly?: boolean;
    marked?: boolean;
    sessionIds?: string[];
    tagKeysOr?: string[];
}
